import React, { useContext, useEffect, useState } from "react";
import classes from "./AllSongs.module.css";
import Song from "../../../types/Song";
import { UserContext } from "../../../contexts/AuthContext";
import { useNavigate , useSearchParams, useLocation} from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

const AllSongs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = Number(searchParams.get("page")) || 1;
  const statusParam = searchParams.get("status") || "ACTIVE";
  const searchParam = searchParams.get("search") || "";
  
const location = useLocation();
  const [page, setPage] = useState(pageParam);
  const [statusFilter, setStatusFilter] = useState(statusParam);
  const [searchTerm, setSearchTerm] = useState(searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [allSongs, setAllSongs] = useState<Song[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [deleting, setDeleting] = useState<string | null>(null);

  const { token } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchSongs = async (pageNumber = 1, status = "ACTIVE", search = "") => {
    try {
      const res = await fetch(
        `${apiUrl}/lyrics/paginated?page=${pageNumber}&limit=10&status=${status}&search=${search}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();

      setAllSongs(data.data);
      setPage(data.page);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error("Error fetching songs:", err);
    }
  };

  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      status: statusFilter,
      search: searchTerm,
    }, { replace: true });
  }, [page, statusFilter, searchTerm]);

  useEffect(() => {
    fetchSongs(page, statusFilter, debouncedSearch);
  }, [page, statusFilter, debouncedSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const navigateToEdit = (song: Song) => {
    navigate("/dashboard/edit-lyrics", {
      state: {
        songId: song.slug,
        from: location.pathname + location.search,
      },
    });
  };

  const handleDelete = async (id: string) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this song?");
    if (!confirmDelete) return;

    setDeleting(id);
    try {
      const res = await fetch(`${apiUrl}/lyrics/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        await fetchSongs(page, statusFilter, searchTerm); // refresh list
      } else {
        const errData = await res.json();
      }
    } catch (error) {
      console.error("Error deleting song:", error);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      <div className={classes.filterContainer}>
        <div className={classes.filterActions}>
          {/* Status Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value={"ACTIVE"}>Active</option>
            <option value={"INACTIVE"}>Inactive</option>
          </select>

          {/* 🔍 Search Box */}
          <input
            type="text"
            placeholder="Search songs..."
            value={searchTerm}
            onChange={handleSearchChange}
            className={classes.searchBox}
          />
        </div>
      </div>

      <div className={classes.songsListingWraper}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Song In Telugu</th>
              <th scope="col">Song In English</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {allSongs.length > 0 ? (
              allSongs.map((song, index) => (
                <tr key={song._id} className={classes.tableRow}>
                  <th scope="row">{(page - 1) * 10 + index + 1}</th>
                  <td>{song.title?.telugu}</td>
                  <td>{song.title?.english}</td>
                  <td>{song.status}</td>
                  <td className="actions">
                    <span
                      className={classes.editBtn}
                      onClick={() => navigateToEdit(song)}
                    >
                      Edit
                    </span>
                    <span
                      className={classes.deleteBtn}
                      onClick={() => handleDelete(song._id!)}
                    >
                      {deleting === song._id ? "Deleting..." : "Delete"}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  No songs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              disabled={page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-2 py-2">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AllSongs;
